import { useQuery } from "@tanstack/react-query";
import { Gauge, Zap } from "lucide-react";

import { datasetsApi } from "@/lib/api-datasets";
import { cn } from "@/lib/cn";

/**
 * Build-time federation hint badge (ADR-0064 Phase 4): a NON-BLOCKING advisory next
 * to a federated dataset — "leggera" (the browse paginates live at any size) vs
 * "pesante" (an aggregation / looked-up-side sort routes to the capped materialise,
 * so a large source should be materialised). Mirrors the runtime `too_heavy` signal.
 *
 * Only renders when the dataset carries a persisted model with joins (`enabled`) —
 * a plain single-source dataset has nothing to hint. Silent on a load error (an
 * advisory must never break the list).
 */
export function FederationHintBadge({
  datasetId,
  enabled = true,
}: {
  datasetId: string;
  enabled?: boolean;
}) {
  const { data } = useQuery({
    queryKey: ["datasets", datasetId, "federation-hint"],
    queryFn: () => datasetsApi.federationHint(datasetId),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
  if (!data) return null;

  const heavy = data.level === "heavy";
  const rows =
    data.approx_rows != null ? ` · ~${data.approx_rows.toLocaleString()} righe` : "";
  const title = (data.reasons.length ? data.reasons.join(" · ") : "Esecuzione live") + rows;

  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        heavy
          ? "border-warning/40 bg-warning/10 text-warning"
          : "border-success/40 bg-success/10 text-success",
      )}
    >
      {heavy ? <Gauge className="h-3 w-3" /> : <Zap className="h-3 w-3" />}
      {heavy ? "Pesante" : "Leggera"}
    </span>
  );
}
